import { FC, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridRowHeightParams,
} from "@mui/x-data-grid-pro";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import parseDetailsToArray from "src/utils/Company/Absences/parseDetailsToArray";
import formatHistoryEntry, {
  getFieldLabel,
} from "src/utils/Company/Absences/formatHistoryEntry";
import {
  AbsenceTypeHistoryEntryDetailsTypes,
  IAbsenceTypeHistoryItemDetails,
  IAbsenceTypeHistoryTableEntry,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface IAbsenceTypeHistoryDetailsProps {
  data: IAbsenceTypeHistoryItemDetails;
}

const AbsenceTypeHistoryDetails: FC<IAbsenceTypeHistoryDetailsProps> = ({
  data,
}) => {
  const logStatus = useLogStatus();

  if (!data) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type history data provided.",
      file: "AbsenceTypeHistoryDetails.tsx",
    });
  }

  const rows = parseDetailsToArray(data);

  const columns = useMemo<GridColDef<IAbsenceTypeHistoryTableEntry>[]>(
    () => [
      {
        field: "id",
        headerName: __("Pole"),
        type: "string",
        width: 400,
        valueFormatter: (value, row) => getFieldLabel(row),
      },
      {
        field: "before",
        headerName: __("Przed zmianą"),
        type: "string",
        width: 300,
        renderCell: ({ row, value }) => formatHistoryEntry(row, value),
      },
      {
        field: "after",
        headerName: __("Po zmianie"),
        type: "string",
        width: 300,
        renderCell: ({ row, value }) => formatHistoryEntry(row, value),
      },
    ],
    []
  );

  const shouldUseAutoHeight = (id: string) =>
    [
      AbsenceTypeHistoryEntryDetailsTypes.ENTITLED_EMPLOYEES,
      AbsenceTypeHistoryEntryDetailsTypes.CONFIGURATION_OPTIONS,
      AbsenceTypeHistoryEntryDetailsTypes.NOTIFICATIONS_CONFIGURATION,
    ].includes(id as AbsenceTypeHistoryEntryDetailsTypes);

  return (
    <Box sx={{ bgcolor: "grey.100", p: "1rem" }}>
      <Typography variant="h6" sx={{ mb: "1rem" }}>
        {__("Szczegóły zmian")}
      </Typography>
      <Paper elevation={2} sx={{ display: "flex", flexDirection: "column" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnSorting
          disableColumnMenu
          disableEval
          hideFooter
          rowSelection={false}
          getRowHeight={(params: GridRowHeightParams) =>
            shouldUseAutoHeight(params.id as string) ? "auto" : undefined
          }
          rowSpacingType="border"
          sx={{
            ".MuiDataGrid-row": {
              borderTop: "1px solid #e0e0e0",
            },
            ".MuiDataGrid-row:nth-of-type(odd)": {
              background: "white",
            },
            ".MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell": {
              paddingTop: "1rem",
              paddingBottom: "1rem",
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default AbsenceTypeHistoryDetails;
